import React, { useEffect, useState, useRef, useLayoutEffect, useTransition } from 'react';
import '../gantt.css';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { differenceInDays, differenceInWeeks, format, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { Tooltip, Typography, IconButton,Card,
    CardContent,Button } from '@mui/material';
import interact from 'interactjs';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Switch from '@mui/material/Switch';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplications';
import DetailsForm from '../components/DetailsForm';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from "react-i18next";
import { 
    enUS, tr, de, fr, es, it, pt, da, sv, fi, nb, nl, pl, cs, sk, hu, ro, bg, el 
  } from 'date-fns/locale';


const Gantt = () => {
    const { t, i18n } = useTranslation();
    const [tasks, setTasks] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [openTasks, setOpenTasks] = useState({});
    const [subtasks, setSubtasks] = useState({});
    const [taskDependencies, setTaskDependencies] = useState([]);
    const [subtaskDependencies, setSubtaskDependencies] = useState([]);
    const { projectId } = useParams();
    const [teams, setTeams] = useState([]);

    const taskRefs = useRef({});
    const subtaskRefs = useRef({});
    const linesContainerRef = useRef(null);
    const navigate = useNavigate();
    const [highlightedTask, setHighlightedTask] = useState(null);
    const [highlightedSubtask, setHighlightedSubtask] = useState(null);
    const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedSubtask, setSelectedSubtask] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [detailsTab, setActiveTab] = useState('details');
    const [userRoleId, setUserRoleId] = useState(null);
    const dayWidth = useRef(80); // İlk değeri günlük mod için 80px olarak ayarlıyoruz
    const isResizingRef = useRef(false);
    const [stickyTop, setStickyTop] = useState(0); // Başlangıçta 100px yukarıdan başlasın

    useEffect(() => {
        const handleScroll = () => {
            // Scroll pozisyonunu al
            const scrollTop = window.scrollY || document.documentElement.scrollTop;

            // Sticky Top'u dinamik olarak ayarla
            setStickyTop(scrollTop - 10);
        };

        // Scroll olayını dinle
        window.addEventListener("scroll", handleScroll);

        return () => {
            // Scroll olayını temizle
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        const headerElement = document.querySelector(".timeline-header");
        if (headerElement) {
            headerElement.style.top = `${stickyTop}px`;
        }
    }, [stickyTop]);

    const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
    });


    const handleViewModeChange = (event) => {
        const newViewMode = event.target.checked ? 'weekly' : 'daily';
        if (newViewMode !== viewMode) {
            setViewMode(newViewMode);
            dayWidth.current = newViewMode === 'weekly' ? 20 : 80;
        }
    };

    const fetchTeams = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/api/teams/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTeams(response.data);
        } catch (error) {
           
        }
    };


    useEffect(() => {
        const fetchGanttData = async () => {
            try {
                const token = localStorage.getItem('token');
                fetchUserRole(projectId, token);

                const response = await api.get(`/api/tasks/${projectId}/ganttdata`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const tasksData = response.data;

                // Görev ve alt görev bağımlılıkları için state güncellemesi

                setTaskDependencies(tasksData.flatMap(task => task.taskDependencies));
                setTasks(sortTasksByDependencies(tasksData, taskDependencies));
                setSubtaskDependencies(tasksData.flatMap(task =>
                    task.subtasks.flatMap(subtask => subtask.subtaskDependencies)
                ));

                // Alt görev bağımlılıklarını sıralayın
                const sortedSubtasks = tasksData.reduce((acc, task) => {
                    const taskSubtasks = sortTasksByDependencies(
                        task.subtasks,
                        task.subtasks.flatMap(subtask => subtask.subtaskDependencies)
                    );
                    acc[task.id] = taskSubtasks;
                    return acc;
                }, {});
                setSubtasks(sortedSubtasks);


                calculateTimeline(tasksData);
                fetchTeams();
            } catch (error) {
                if (error.response && error.response.status === 401) {
                   
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/login');
                }
                else if (error.response?.status === 403) {
                    alert(t("access_denied"));
                    navigate('/projects'); // Kullanıcıyı genel proje sayfasına yönlendir
                }

                else {
                    
                }
            }
        };

        fetchGanttData();
    }, [projectId, detailsModalOpen]);





    // initialPosition ve task tarih referansları bileşen seviyesinde tanımlanır
    const initialPosition = useRef(null);
    const taskStartDateRef = useRef(null);
    const taskDueDateRef = useRef(null);

    useEffect(() => {
        let isDragging = false;
        let isResizing = false;
        const isUserRestricted = userRoleId === 2 || userRoleId === 3;
        // Task'lar için drag işlemi
        Object.values(taskRefs.current).forEach(taskBar => {
            if (taskBar && !isUserRestricted) {
                let deltaX = 0;
                let newStartDate = null;
                let newDueDate = null;

                // Drag işlemi
                interact(taskBar)
                    .draggable({
                        listeners: {
                            start: (event) => {
                                if (isResizing) return;  // Eğer resize oluyorsa drag'i iptal et
                                isDragging = true;

                                const target = event.target;
                                initialPosition.current = {
                                    left: parseFloat(target.style.left) || 0,
                                    startX: event.clientX,
                                };
                               
                                if (target.dataset.startDate && target.dataset.dueDate) {
                                    taskStartDateRef.current = new Date(target.dataset.startDate);
                                    taskDueDateRef.current = new Date(target.dataset.dueDate);
                                }
                                //console.log("Initial taskstartdateref:", taskStartDateRef.current);
                               
                            },
                            move: (event) => {
                                if (isResizing) return;

                                const target = event.target;
                                deltaX = event.clientX - initialPosition.current.startX;
                                const dx = deltaX / dayWidth.current
                                target.style.left = `${initialPosition.current.left + deltaX}px`;
                                newStartDate = addDays(taskStartDateRef.current, dx);
                                newDueDate = addDays(taskDueDateRef.current, dx);
                            },
                            end: (event) => {
                                if (isResizing) return;

                                const target = event.target;
                                const taskId = event.target.dataset.taskId;
                                //const dayWidth = 80;
                                const dayOffset = Math.round(deltaX / dayWidth.current);
                                const roundedLeft = Math.round(initialPosition.current.left / dayWidth.current) * dayWidth.current;

                                newStartDate = addDays(taskStartDateRef.current, dayOffset);
                                const taskDuration = differenceInDays(taskDueDateRef.current, taskStartDateRef.current);
                                newDueDate = addDays(newStartDate, taskDuration);

                                

                                if (!isNaN(newStartDate.getTime()) && !isNaN(newDueDate.getTime())) {
                                    updateTaskDates(taskId, newStartDate, newDueDate, 'drag');
                                }

                                target.style.left = `${roundedLeft}px`; // Barı en yakın güne hizala
                                isDragging = false;
                            }
                        }
                    });

                // Resize işlemi
                let originalWidth;
                // Resize işlemi
                interact(taskBar).resizable({
                    edges: { left: false, right: true, bottom: false, top: false },
                    listeners: {
                        start: (event) => {
                            if (isDragging) return;  // Eğer drag oluyorsa resize'i iptal et
                            isResizing = true;
                            isResizingRef.current = true;
                            const target = event.target;
                            originalWidth = target.style.width; // Orijinal genişliği kaydediyoruz

                            if (target.dataset.startDate && target.dataset.dueDate) {
                                taskStartDateRef.current = new Date(target.dataset.startDate);
                            }

                        },
                        move: (event) => {
                            if (isDragging) return;

                            const target = event.target;
                            const width = parseFloat(target.style.width) || 0;
                            deltaX = event.deltaRect.width;

                            target.style.width = `${width + deltaX}px`;
                        },
                        end: async (event) => {
                            if (isDragging) return;

                            const target = event.target;
                            const taskId = target.dataset.taskId;
                            const finalWidth = parseFloat(target.style.width) || 0;
                            const dayOffset = Math.round(finalWidth / dayWidth.current);
                            newDueDate = addDays(taskStartDateRef.current, dayOffset);

                            if (!isNaN(newDueDate.getTime())) {
                                // updateTaskDates çağrısını bekleyin
                                const isSuccessful = await updateTaskDates(taskId, taskStartDateRef.current, newDueDate, 'resize');
                                isResizingRef.current = false;
                                // İşlem başarısızsa, çubuğu orijinal genişliğe döndür ve hata mesajı göster
                                if (!isSuccessful) {
                                    target.style.width = originalWidth;
                                } else {
                                    // İşlem başarılıysa, genişliği en yakın güne hizala
                                    target.style.width = `${dayOffset * dayWidth.current}px`;
                                }
                            } else {
                                // Geçersiz tarih durumunda, çubuğu orijinal genişliğe döndür
                                target.style.width = originalWidth;
                            }

                            isResizing = false;
                        }
                    }
                });

            }
        });

        // Subtask'lar için drag ve resize işlemleri
        Object.values(subtaskRefs.current).forEach(subtaskBar => {
            if (subtaskBar && !isUserRestricted) {
                let deltaX = 0;
                let newStartDate = null;
                let newDueDate = null;

                // Subtask drag işlemi
                interact(subtaskBar)
                    .draggable({
                        listeners: {
                            start: (event) => {
                                if (isResizing) return;  // Eğer resize oluyorsa drag'i iptal et
                                isDragging = true;

                                const target = event.target;
                                initialPosition.current = {
                                    left: parseFloat(target.style.left) || 0,
                                    startX: event.clientX,
                                };

                                if (target.dataset.startDate && target.dataset.dueDate) {
                                    taskStartDateRef.current = new Date(target.dataset.startDate);
                                    taskDueDateRef.current = new Date(target.dataset.dueDate);
                                }

                            },
                            move: (event) => {
                                if (isResizing) return;

                                const target = event.target;
                                deltaX = event.clientX - initialPosition.current.startX;
                                const dx = deltaX / dayWidth.current; //80;

                                target.style.left = `${initialPosition.current.left + deltaX}px`;
                                newStartDate = addDays(taskStartDateRef.current, dx);
                                newDueDate = addDays(taskDueDateRef.current, dx);
                            },
                            end: (event) => {
                                if (isResizing) return;

                                const target = event.target;
                                const subtaskId = event.target.dataset.taskId;

                                //const dayWidth = 80;
                                const dayOffset = Math.round(deltaX / dayWidth.current);
                                const roundedLeft = Math.round(initialPosition.current.left / dayWidth.current) * dayWidth.current;

                                newStartDate = addDays(taskStartDateRef.current, dayOffset);
                                const taskDuration = differenceInDays(taskDueDateRef.current, taskStartDateRef.current);
                                newDueDate = addDays(newStartDate, taskDuration);


                                if (!isNaN(newStartDate.getTime()) && !isNaN(newDueDate.getTime())) {
                                    updateSubtaskDates(subtaskId, newStartDate, newDueDate, 'drag');
                                }

                                target.style.left = `${roundedLeft}px`; // Barı en yakın güne hizala
                                isDragging = false;
                            }
                        }
                    });

                // Subtask resize işlemi
                // Subtask resize işlemi
                let originalWidth;
                interact(subtaskBar).resizable({
                    edges: { left: false, right: true, bottom: false, top: false },
                    listeners: {
                        start: (event) => {
                            if (isDragging) return;  // Eğer drag oluyorsa resize işlemini iptal et
                            isResizing = true;

                            const target = event.target;
                            originalWidth = target.style.width; // Orijinal genişliği her resize başlangıcında kaydet

                            if (target.dataset.startDate && target.dataset.dueDate) {
                                taskStartDateRef.current = new Date(target.dataset.startDate);
                            }

                        },
                        move: (event) => {
                            if (isDragging) return;

                            const target = event.target;
                            const width = parseFloat(target.style.width) || 0;
                            deltaX = event.deltaRect.width;

                            target.style.width = `${width + deltaX}px`;
                        },
                        end: async (event) => {
                            if (isDragging) return;

                            const target = event.target;
                            const subtaskId = target.dataset.taskId;

                            // Yeni genişlik hesaplama
                            const finalWidth = parseFloat(target.style.width) || 0;
                            const dayOffset = Math.round(finalWidth / dayWidth.current);

                            // Yeni bitiş tarihi hesapla
                            const newDueDate = addDays(taskStartDateRef.current, dayOffset);

                            if (!isNaN(newDueDate.getTime())) {
                                // updateSubtaskDates çağrısını bekleyin
                                const isSuccessful = await updateSubtaskDates(subtaskId, taskStartDateRef.current, newDueDate, 'resize');

                                // Güncelleme başarısızsa, çubuğu orijinal genişliğe döndür ve hata mesajı göster
                                if (!isSuccessful) {
                                    target.style.width = originalWidth; // İlk olarak genişliği eski haline döndür
                                } else {
                                    // Başarıyla güncellenirse, genişliği en yakın güne hizala
                                    target.style.width = `${dayOffset * dayWidth.current}px`;
                                }
                            } else {
                                // Geçersiz tarih durumunda, çubuğu orijinal genişliğe döndür
                                target.style.width = originalWidth;
                            }

                            isResizing = false;
                        }
                    }
                });


            }
        });
    }, [tasks, subtasks]);


    useLayoutEffect(() => {
        if (tasks.length > 0 && taskDependencies.length > 0) {
            const sortedTasks = sortTasksByDependencies(tasks, taskDependencies);
            clearDependencyMarkers();
            renderDependencyMarkers(sortedTasks, taskDependencies);

        }
        if (Object.keys(openTasks).length > 0 && subtaskDependencies.length > 0) {
            Object.keys(openTasks).forEach((taskId) => {
                if (openTasks[taskId]) {
                    const taskSubtasks = subtasks[taskId];
                    if (taskSubtasks) {
                        const relevantSubtaskDependencies = subtaskDependencies.filter(dep =>
                            taskSubtasks.some(subtask => subtask.id === dep.subtaskId || subtask.id === dep.dependentSubtaskId)
                        );
                        const sortedSubtasks = sortTasksByDependencies(taskSubtasks, relevantSubtaskDependencies);
                        setTimeout(() => {
                            renderDependencyMarkers(sortedSubtasks, relevantSubtaskDependencies, true);
                        }, 0);
                    }
                }
            });
        }
    }, [tasks, taskDependencies, subtasks, subtaskDependencies, openTasks]);



    useEffect(() => {
        if (tasks.length > 0 && taskDependencies.length > 0) {
            // `transitionend` veya `animationend` tetiklendiğinde çizimleri güncelle
            const handleTransitionEnd = () => {
                //console.log("Animasyon veya geçiş tamamlandı, dependency çizgileri yeniden çiziliyor...");
                clearDependencyMarkers();

                calculateTimeline(tasks);
                renderDependencyMarkers(tasks, taskDependencies);

                // Subtask bağımlılıklarını da çiz
                Object.keys(openTasks).forEach((taskId) => {
                    if (openTasks[taskId]) {
                        const taskSubtasks = subtasks[taskId];
                        if (taskSubtasks) {
                            const relevantSubtaskDependencies = subtaskDependencies.filter(dep =>
                                taskSubtasks.some(subtask => subtask.id === dep.subtaskId || subtask.id === dep.dependentSubtaskId)
                            );
                            const sortedSubtasks = sortTasksByDependencies(taskSubtasks, relevantSubtaskDependencies);
                            renderDependencyMarkers(sortedSubtasks, relevantSubtaskDependencies, true);
                        }
                    }
                });
            };

            // Tüm `gantt-task-bar` ve `gantt-subtask-bar` elemanlarına `transitionend` ve `animationend` dinleyicileri ekleyin
            const taskBarElements = document.querySelectorAll('.gantt-task-bar, .gantt-subtask-bar');

            // Stil değişimlerini kontrol ederek sadece animasyon veya geçiş varsa dinleyici ekleyin
            taskBarElements.forEach((element) => {
                const computedStyle = window.getComputedStyle(element);

                if (computedStyle.transitionDuration !== '0s' || computedStyle.animationDuration !== '0s') {
                    element.addEventListener('transitionend', handleTransitionEnd);
                    element.addEventListener('animationend', handleTransitionEnd); // Eğer animasyon varsa bunu da dinleyin
                }
            });

            return () => {
                // Dinleyicileri temizleyin
                taskBarElements.forEach((element) => {
                    element.removeEventListener('transitionend', handleTransitionEnd);
                    element.removeEventListener('animationend', handleTransitionEnd);
                });
                //console.log("Transitionend ve animationend dinleyicileri temizlendi.");
            };
        } else {
            clearDependencyMarkers();

            calculateTimeline(tasks);
            renderDependencyMarkers(tasks, taskDependencies);
        }
    }, [tasks, taskDependencies, subtasks, subtaskDependencies, openTasks, viewMode]);




    const getBarStyle = (start_date, due_date, status) => {
        let backgroundColor = '';

        const isOverdue = new Date(due_date) < new Date() && status !== 'completed';

        if (isOverdue) {
            backgroundColor = '#B85B5F'; // Overdue tasks
        } else {
            switch (status) {
                case 'to-do':
                    backgroundColor = '#96A7AD';
                    break;
                case 'in-progress':
                    backgroundColor = '#7E84F7';
                    break;
                case 'completed':
                    backgroundColor = '#4ECC44';
                    break;
                default:
                    backgroundColor = 'grey'; // Default for unknown statuses
            }
        }

        return {
            left: calculateLeftPosition(start_date),
            width: calculateWidth(start_date, due_date),
            backgroundColor, // Add color coding based on status
        };
    };


    const fetchAllTaskDependencies = async (tasks) => {
        try {
            const token = localStorage.getItem('token');
            const dependencies = [];

            const dependencyPromises = tasks.map(async (task) => {
                const response = await api.get(`/api/taskdependencies/${task.id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                dependencies.push(...response.data);
            });

            await Promise.all(dependencyPromises);
            return dependencies;
        } catch (error) {
            return [];
        }
    };

    const fetchAllSubtaskDependencies = async (subtasks) => {
        try {
            const token = localStorage.getItem('token');
            const dependencies = [];

            const subtaskPromises = subtasks.map(async (subtask) => {
                const response = await api.get(`/api/subtaskdependencies/${subtask.id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                dependencies.push(...response.data);
            });

            await Promise.all(subtaskPromises);
            return dependencies;
        } catch (error) {
            return [];
        }
    };

    const localeMap = {
        en: enUS,  // İngilizce
        tr: tr,    // Türkçe
        de: de,    // Almanca
        fr: fr,    // Fransızca
        es: es,    // İspanyolca
        it: it,    // İtalyanca
        pt: pt,    // Portekizce
        da: da,    // Danca
        sv: sv,    // İsveççe
        fi: fi,    // Fince
        nb: nb,    // Norveççe (Bokmål)
        nl: nl,    // Hollandaca
        pl: pl,    // Lehçe
        cs: cs,    // Çekçe
        sk: sk,    // Slovakça
        hu: hu,    // Macarca
        ro: ro,    // Rumence
        bg: bg,    // Bulgarca
        el: el     // Yunanca
    };
    
    // Varsayılan dil İngilizce, ama eğer `i18n.language` tanımlıysa uygun dili kullan.
    const currentLocale = localeMap[i18n.language] || enUS;

    const calculateTimeline = (tasks) => {
        if (tasks && tasks.length > 0) {
            const startDates = tasks.map(task => new Date(task.start_date));
            const endDates = tasks.map(task => new Date(task.due_date));
            const minDate = new Date(Math.min(...startDates));
            const maxDate = new Date(Math.max(...endDates));
            const timelineArray = { topRow: [], bottomRow: [] };

            if (viewMode === 'daily') { 
                let currentDate = minDate;
                while (currentDate <= maxDate) {
                    timelineArray.topRow.push(format(currentDate, 'MMM d', { locale: currentLocale })); // Ay ve gün
                    timelineArray.bottomRow.push(format(currentDate, 'EEE', { locale: currentLocale })); // Gün kısaltmaları: Mon, Tue, Wed...
                    currentDate = addDays(currentDate, 1);
                }
            } else if (viewMode === 'weekly') {
                const startWeek = startOfWeek(minDate, { weekStartsOn: 1 }); // Pazartesi haftanın ilk günü
                const endWeek = endOfWeek(maxDate, { weekStartsOn: 1 });
            
                let currentWeek = startWeek;
                while (currentWeek <= endWeek) {
                    const start = format(currentWeek, 'MMM d', { locale: currentLocale });
                    const end = format(addDays(currentWeek, 6), 'MMM d', { locale: currentLocale });
                    timelineArray.topRow.push(`${start} - ${end}`);
            
                    // Alt satır için günlerin çevirisini `i18next` üzerinden alalım
                    timelineArray.bottomRow.push([
                        t('days.monday_short'),
                        t('days.tuesday_short'),
                        t('days.wednesday_short'),
                        t('days.thursday_short'),
                        t('days.friday_short'),
                        t('days.saturday_short'),
                        t('days.sunday_short')
                    ]);
            
                    currentWeek = addDays(currentWeek, 7);
                }
            }
            

            setTimeline(timelineArray);
        } else {
            setTimeline({ topRow: [], bottomRow: [] });
        }
    };


    const updateTaskDates = async (taskId, newStartDate, newDueDate, mode) => {
        try {
            const token = localStorage.getItem('token');
            const taskToUpdate = tasks.find(task => task.id === Number(taskId));

            // Task'ın mevcut tarihleri ile yeni tarihlerini karşılaştır
            const currentStartDate = new Date(taskToUpdate.start_date);
            const currentDueDate = new Date(taskToUpdate.due_date);

            if (
                currentStartDate.getTime() === new Date(newStartDate).getTime() &&
                currentDueDate.getTime() === new Date(newDueDate).getTime()
            ) {
                // Eğer tarihler aynıysa, güncellemeyi atla
                return;
            }


            // Tüm alt görevlerin bitiş tarihlerinin yeni bitiş tarihini aşmadığını kontrol et
            // Resize dışındayken alt görev sınırlarını kontrol et
            if (isResizingRef.current) {
                const subtasksForTask = subtasks[taskId] || [];
                const newDueDateObj = new Date(newDueDate);
                for (let subtask of subtasksForTask) {
                    const subtaskDueDate = new Date(subtask.due_date);
                    if (subtaskDueDate > newDueDateObj) {
                        toast.error(t("move_canceled_subtasks"), {
                            position: 'top-right',
                            autoClose: 5000,
                        });
                        return false;
                    }
                }
            }

            // Kaydırma miktarını hesapla (kaç gün kaydırıldığını)
            const startDateDifference = differenceInDays(new Date(newStartDate), currentStartDate);

            // Task'ı güncelle
            const response = await api.patch(`/api/tasks/${taskId}`, {
                start_date: newStartDate,
                due_date: newDueDate,
                mode,
                ganttMode: true
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const updatedTask = response.data;

            // Subtask'ları kaydırma fonksiyonunu çağır
            await updateSubtasksForTask(taskId, startDateDifference);

            // Güncellenen görevleri yerel state'de güncelle
            setTasks(prevTasks => {
                const updatedTasks = [...prevTasks];
                const index = updatedTasks.findIndex(task => task.id === Number(taskId));

                if (index !== -1) {
                    updatedTasks[index] = { ...updatedTasks[index], ...updatedTask };
                }

                // Bağımlı task'ları güncelleme
                const dependenciesToUpdate = taskDependencies.filter(dep => Number(dep.taskId) === Number(taskId));

                dependenciesToUpdate.forEach(dep => {
                    const dependentTaskId = dep.dependentTaskId;
                    const dependentTask = updatedTasks.find(task => task.id === dependentTaskId);

                    if (dependentTask) {
                        let updatedStartDate = new Date(dependentTask.start_date);
                        let updatedDueDate = new Date(dependentTask.due_date);
                        const originalDuration = differenceInDays(new Date(dependentTask.due_date), new Date(dependentTask.start_date));

                        // Bağımlılık türüne göre kuralları kontrol ediyoruz
                        switch (dep.dependencyType) {
                            case 'Finish-to-Start':
                                if (new Date(newDueDate) > new Date(dependentTask.start_date)) {
                                    updatedStartDate = new Date(newDueDate); // Ana task bitiş tarihi bağımlı task'tan daha ileriye giderse
                                    updatedDueDate = addDays(updatedStartDate, originalDuration); // Süreyi koruyarak bitiş tarihini güncelle
                                }
                                break;
                            case 'Start-to-Start':
                                if (new Date(newStartDate) > new Date(dependentTask.start_date)) {
                                    updatedStartDate = new Date(newStartDate); // Ana task'ın başlangıcı bağımlı task'tan önce olamaz
                                    updatedDueDate = addDays(updatedStartDate, originalDuration); // Süreyi koru
                                }
                                break;
                            case 'Finish-to-Finish':
                                if (new Date(newDueDate) > new Date(dependentTask.due_date)) {
                                    updatedDueDate = new Date(newDueDate); // Ana task'ın bitişi bağımlı task'tan önce olamaz
                                    updatedStartDate = addDays(updatedDueDate, -originalDuration); // Süreyi koruyarak başlangıç tarihini güncelle
                                }
                                break;
                            case 'Start-to-Finish':
                                if (new Date(newStartDate) > new Date(dependentTask.due_date)) {
                                    updatedDueDate = new Date(newStartDate); // Bağımlı task, ana task başlamadan bitemez
                                    updatedStartDate = addDays(updatedDueDate, -originalDuration); // Süreyi koruyarak başlangıç tarihini güncelle
                                }
                                break;
                        }

                        // Eğer tarih değişikliği gerekiyorsa güncelle
                        if (
                            new Date(dependentTask.start_date).getTime() !== updatedStartDate.getTime() ||
                            new Date(dependentTask.due_date).getTime() !== updatedDueDate.getTime()
                        ) {
                            updateTaskDates(dependentTaskId, updatedStartDate, updatedDueDate, 'auto');
                        }
                    }
                });

                // Reverse dependency task'ları güncelleme (dependent task geri alındığında)
                const dependenciesFromUpdate = taskDependencies.filter(dep => Number(dep.dependentTaskId) === Number(taskId));

                dependenciesFromUpdate.forEach(dep => {
                    const mainTaskId = dep.taskId;
                    const mainTask = updatedTasks.find(task => task.id === mainTaskId);

                    if (mainTask) {
                        let updatedMainStartDate = new Date(mainTask.start_date);
                        let updatedMainDueDate = new Date(mainTask.due_date);
                        const originalDuration = differenceInDays(new Date(mainTask.due_date), new Date(mainTask.start_date));

                        // Ana task'ın güncellenmesi gereken durumları kontrol et
                        switch (dep.dependencyType) {
                            case 'Finish-to-Start':
                                if (new Date(mainTask.due_date) > new Date(newStartDate)) {
                                    updatedMainDueDate = new Date(newStartDate); // Bağımlı task, ana task'tan önce başlayamaz
                                    updatedMainStartDate = addDays(updatedMainDueDate, -originalDuration); // Süreyi koru
                                }
                                break;
                            case 'Start-to-Start':
                                if (new Date(mainTask.start_date) > new Date(newStartDate)) {
                                    updatedMainStartDate = new Date(newStartDate); // Aynı anda başlamalılar
                                    updatedMainDueDate = addDays(updatedMainStartDate, originalDuration); // Süreyi koru
                                }
                                break;
                            case 'Finish-to-Finish':
                                if (new Date(mainTask.due_date) > new Date(newDueDate)) {
                                    updatedMainDueDate = new Date(newDueDate); // Aynı anda bitmeli
                                    updatedMainStartDate = addDays(updatedMainDueDate, -originalDuration); // Süreyi koru
                                }
                                break;
                            case 'Start-to-Finish':
                                if (new Date(mainTask.start_date) > new Date(newDueDate)) {
                                    updatedMainStartDate = new Date(newDueDate); // Bağımlı task, ana task başlamadan bitemez
                                    updatedMainDueDate = addDays(updatedMainStartDate, originalDuration); // Süreyi koru
                                }
                                break;
                        }

                        // Ana task'ta tarih değişikliği varsa güncelle
                        if (
                            new Date(mainTask.start_date).getTime() !== updatedMainStartDate.getTime() ||
                            new Date(mainTask.due_date).getTime() !== updatedMainDueDate.getTime()
                        ) {
                            updateTaskDates(mainTaskId, updatedMainStartDate, updatedMainDueDate, 'auto');
                        }
                    }
                });

                const reorderedTasks = sortTasksByDependencies(updatedTasks, taskDependencies);
                return reorderedTasks;
            });

            clearDependencyMarkers(); // Dependency çizgilerini temizleyip yeniden çiziyoruz
            return true;

        } catch (error) {
            
            return false;
        }
    };



    const updateSubtasksForTask = async (taskId, startDateDifference) => {
        try {
            const subtasksList = subtasks[taskId];
            if (subtasksList && subtasksList.length > 0) {
                const token = localStorage.getItem('token');

                // Subtask'ları kaydır ve yeni tarihleri hesapla
                const updatedSubtasks = subtasksList.map(subtask => {
                    const newStartDate = addDays(new Date(subtask.start_date), startDateDifference);
                    const newDueDate = addDays(new Date(subtask.due_date), startDateDifference);
                    return {
                        ...subtask,
                        start_date: newStartDate,
                        due_date: newDueDate
                    };
                });

                // Backend'e güncellemeleri gönder
                await Promise.all(updatedSubtasks.map(async (subtask) => {
                    const response = await api.patch(`/api/subtasks/${subtask.id}`, {
                        start_date: subtask.start_date,
                        due_date: subtask.due_date,
                        ganttMode: true // GanttMode parametresi ile gönderiyoruz
                    }, {
                        headers: { Authorization: `Bearer ${token}` },
                    });

                    if (response.status !== 200 && response.status !== 204) {
                       
                    }
                }));

                // Durum güncellemesi
                setSubtasks(prevSubtasks => ({
                    ...prevSubtasks,
                    [taskId]: updatedSubtasks
                }));


            }
        } catch (error) {
            
        }
    };


    const updateSubtaskDates = async (subtaskId, newStartDate, newDueDate, mode) => {
        let isSuccessful = false;
        try {
            const token = localStorage.getItem('token');




            const updatedSubtasks = { ...subtasks };
            const parentTaskId = Object.keys(updatedSubtasks).find(taskId =>
                updatedSubtasks[taskId].some(subtask => subtask.id === Number(subtaskId))
            );

            if (!parentTaskId) {

                return isSuccessful;
            }

            const parentTask = tasks.find(task => task.id === Number(parentTaskId));
            const parentTaskStartDate = new Date(parentTask.start_date);
            const parentTaskDueDate = new Date(parentTask.due_date);

            const subtaskIndex = updatedSubtasks[parentTaskId].findIndex(subtask => subtask.id === Number(subtaskId));
            if (subtaskIndex === -1) {

                return isSuccessful;
            }

            let updatedStartDate = new Date(newStartDate);
            let updatedDueDate = new Date(newDueDate);

            // Parent task boundary checks
            if (updatedStartDate < parentTaskStartDate || updatedDueDate > parentTaskDueDate) {

                toast.error(t("subtask_move_canceled"), {
                    position: 'top-right',
                    autoClose: 5000,
                });
                return isSuccessful;
            }


            // Filter dependencies related to the current subtask
            const dependenciesToCheck = subtaskDependencies.filter(dep =>
                Number(dep.subtaskId) === Number(subtaskId) ||
                Number(dep.dependentSubtaskId) === Number(subtaskId)
            );



            let dependencyViolation = false;

            // Check dependencies for violation
            dependenciesToCheck.forEach(dep => {


                if (Number(dep.subtaskId) === Number(subtaskId)) {
                    const dependentSubtask = updatedSubtasks[parentTaskId].find(subtask => subtask.id === dep.dependentSubtaskId);
                    if (dependentSubtask) {
                        let dependentStartDate = new Date(dependentSubtask.start_date);
                        let dependentDueDate = new Date(dependentSubtask.due_date);

                        // Dependency rules
                        switch (dep.dependencyType) {
                            case 'Finish-to-Start':
                                if (updatedDueDate > dependentStartDate) {

                                    dependencyViolation = true;
                                    toast.error(t("rule_violation_fs"), {
                                        position: 'top-right',
                                        autoClose: 5000,
                                    });
                                }
                                break;
                            case 'Start-to-Start':
                                if (updatedStartDate > dependentStartDate) {

                                    dependencyViolation = true;
                                    toast.error(t("rule_violation_ss"), {
                                        position: 'top-right',
                                        autoClose: 5000,
                                    });
                                }
                                break;
                            case 'Finish-to-Finish':
                                if (updatedDueDate > dependentDueDate) {

                                    dependencyViolation = true;
                                    toast.error(t("rule_violation_ff"), {
                                        position: 'top-right',
                                        autoClose: 5000,
                                    });
                                }
                                break;
                            case 'Start-to-Finish':
                                if (updatedStartDate > dependentDueDate) {

                                    dependencyViolation = true;
                                    toast.error(t("rule_violation_sf"), {
                                        position: 'top-right',
                                        autoClose: 5000,
                                    });
                                }
                                break;
                        }
                    }
                } else if (Number(dep.dependentSubtaskId) === Number(subtaskId)) {
                    const mainSubtask = updatedSubtasks[parentTaskId].find(subtask => subtask.id === dep.subtaskId);
                    if (mainSubtask) {
                        let mainStartDate = new Date(mainSubtask.start_date);
                        let mainDueDate = new Date(mainSubtask.due_date);

                        // Check rules for main subtask
                        switch (dep.dependencyType) {
                            case 'Finish-to-Start':
                                //if (updatedDueDate < mainStartDate) {
                                if (updatedStartDate < mainDueDate) {

                                    dependencyViolation = true;
                                    toast.error("Rule violation: Finish-to-Start.", {
                                        position: 'top-right',
                                        autoClose: 5000,
                                    });
                                }
                                break;
                            case 'Start-to-Start':
                                if (updatedStartDate < mainStartDate) {

                                    dependencyViolation = true;
                                    toast.error("Rule violation: Start-to-Start.", {
                                        position: 'top-right',
                                        autoClose: 5000,
                                    });
                                }
                                break;
                            case 'Finish-to-Finish':
                                if (updatedDueDate < mainDueDate) {

                                    dependencyViolation = true;
                                    toast.error("Rule violation: Finish-to-Finish.", {
                                        position: 'top-right',
                                        autoClose: 5000,
                                    });
                                }
                                break;
                            case 'Start-to-Finish':
                                if (updatedStartDate < mainDueDate) {

                                    dependencyViolation = true;
                                    toast.error("Rule violation: Start-to-Finish.", {
                                        position: 'top-right',
                                        autoClose: 5000,
                                    });
                                }
                                break;
                        }
                    }
                }
            });

            // Cancel the update if any violation is found
            if (dependencyViolation) {

                return isSuccessful;
            }

            // Update the subtask in the database
            const response = await api.patch(`/api/subtasks/${subtaskId}`, {
                start_date: updatedStartDate,
                due_date: updatedDueDate,
                ganttMode: true
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200 || response.status === 204) {
                isSuccessful = true;

            }

            // Update state after successful update
            setSubtasks(prevSubtasks => {
                const updatedSubtasks = { ...prevSubtasks };
                updatedSubtasks[parentTaskId][subtaskIndex] = {
                    ...updatedSubtasks[parentTaskId][subtaskIndex],
                    start_date: updatedStartDate,
                    due_date: updatedDueDate
                };
                return updatedSubtasks;
            });

            clearDependencyMarkers(); // Redraw dependency lines
            return isSuccessful;

        } catch (error) {
            
        }
        return isSuccessful;
    };



    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.slice(0, maxLength) + '...';
        }
        return text;
    };
    const renderDependencyMarkers = (sortedTasks, dependencies, isSubtask = false) => {
        dependencies.forEach((dependency, index) => {
            const fromTask = sortedTasks.find(task => task.id === (isSubtask ? dependency.subtaskId : dependency.taskId));
            const toTask = sortedTasks.find(task => task.id === (isSubtask ? dependency.dependentSubtaskId : dependency.dependentTaskId));

            // Eğer fromTask veya toTask bulunamazsa logları kontrol edelim
            if (!fromTask) {
                
                return;
            }
            if (!toTask) {
                
                return;
            }

            // DOM elemanları üzerinden referansları alalım
            const fromItem = isSubtask ? subtaskRefs.current[fromTask.id] : taskRefs.current[fromTask.id];
            const toItem = isSubtask ? subtaskRefs.current[toTask.id] : taskRefs.current[toTask.id];

            // DOM elemanları henüz render edilmediyse bu çizim yapılmamalı
            if (!fromItem || !toItem) {
                
                return; // Eğer DOM elemanı yoksa çizim yapılmaz.
            }
            const fromRect = fromItem.getBoundingClientRect();
            const toRect = toItem.getBoundingClientRect();

            const svgRect = linesContainerRef.current.getBoundingClientRect();

            const fromTop = fromRect.top + fromRect.height / 2 - svgRect.top;
            const toTop = toRect.top + toRect.height / 2 - svgRect.top;
            // Çizgilerin hangi noktada başladığını ve bittiğini hesaplamak için offset ve pozisyonları kullanıyoruz
            const fromLeft = calculateLeftPosition(fromTask.start_date);
            const fromRight = fromLeft + fromItem.offsetWidth;
            //const fromTop = fromItem.offsetTop + fromItem.offsetHeight / 2 - linesContainerRef.current.getBoundingClientRect().top;



            const toLeft = calculateLeftPosition(toTask.start_date);
            const toRight = toLeft + toItem.offsetWidth;
            // const toTop = toItem.offsetTop + toItem.offsetHeight / 2 - linesContainerRef.current.getBoundingClientRect().top;

            let color;
            let pathSegments = [];
            const horizontalPadding = 10;
            const verticalOffset = 20;

            switch (dependency.dependencyType) {
                case 'Start-to-Start':
                    color = 'red';
                    pathSegments.push(`M${fromLeft} ${fromTop}`);
                    pathSegments.push(`H${fromLeft - horizontalPadding}`);
                    if (fromTop !== toTop) {
                        pathSegments.push(`V${fromTop + (fromTop < toTop ? verticalOffset : -verticalOffset)}`);
                        pathSegments.push(`H${toLeft - horizontalPadding}`);
                        pathSegments.push(`V${toTop}`);
                    }
                    pathSegments.push(`H${toLeft}`);
                    appendToDOM(createArrow(toLeft, toTop, color, 'right'));
                    break;

                case 'Finish-to-Start':
                    color = 'orange';
                    pathSegments.push(`M${fromRight} ${fromTop}`);
                    pathSegments.push(`H${fromRight + horizontalPadding}`);
                    if (fromTop !== toTop) {
                        pathSegments.push(`V${fromTop + (fromTop < toTop ? verticalOffset : -verticalOffset)}`);
                        pathSegments.push(`H${toLeft - horizontalPadding}`);
                        pathSegments.push(`V${toTop}`);
                    }
                    pathSegments.push(`H${toLeft}`);
                    appendToDOM(createArrow(toLeft, toTop, color, 'right'));
                    break;

                case 'Finish-to-Finish':
                    color = 'blue';
                    pathSegments.push(`M${fromRight} ${fromTop}`);
                    pathSegments.push(`H${fromRight + horizontalPadding}`);
                    if (fromTop !== toTop) {
                        pathSegments.push(`V${fromTop + (fromTop < toTop ? verticalOffset : -verticalOffset)}`);
                        pathSegments.push(`H${toRight + horizontalPadding}`);
                        pathSegments.push(`V${toTop}`);
                    }
                    pathSegments.push(`H${toRight}`);
                    appendToDOM(createArrow(toRight, toTop, color, 'left'));
                    break;

                case 'Start-to-Finish':
                    color = 'green';
                    pathSegments.push(`M${fromLeft} ${fromTop}`);
                    pathSegments.push(`H${fromLeft - horizontalPadding}`);
                    if (fromTop !== toTop) {
                        pathSegments.push(`V${fromTop + (fromTop < toTop ? verticalOffset : -verticalOffset)}`);
                        pathSegments.push(`H${toRight + horizontalPadding}`);
                        pathSegments.push(`V${toTop}`);
                    }
                    pathSegments.push(`H${toRight}`);
                    appendToDOM(createArrow(toRight, toTop, color, 'left'));
                    break;
            }

            // Çizgiyi oluşturup DOM'a ekleme
            if (pathSegments.length > 0) {
                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                path.setAttribute('d', pathSegments.join(' '));
                path.setAttribute('stroke', color);
                path.setAttribute('stroke-width', '2');
                path.setAttribute('fill', 'none');
                appendToDOM(path);
            }
        });
    };

    // Çizgileri DOM'a ekleme fonksiyonu
    const appendToDOM = (element) => {
        const svg = document.querySelector('.gantt-dependency-lines');
        if (element.tagName === 'path') {
            svg.appendChild(element);
        } else {
            document.querySelector('.gantt-tasks').appendChild(element);
        }
    };

    // Ok işaretini oluşturma fonksiyonu
    const createArrow = (left, top, color, direction = 'right') => {
        const arrow = document.createElement('div');
        arrow.className = 'dependency-arrow';
        arrow.style.position = 'absolute';
        arrow.style.width = 0;
        arrow.style.height = 0;
        arrow.style.borderLeft = '5px solid transparent';
        arrow.style.borderRight = '5px solid transparent';

        switch (direction) {
            case 'right':
                arrow.style.borderTop = `5px solid ${color}`;
                arrow.style.left = `${left - 5}px`;
                arrow.style.top = `${top - 2}px`;
                arrow.style.transform = 'rotate(270deg)';
                break;
            case 'left':
                arrow.style.borderTop = `5px solid ${color}`;
                arrow.style.left = `${left - 2}px`;
                arrow.style.top = `${top - 2}px`;
                arrow.style.transform = 'rotate(90deg)';
                break;
            case 'down':
                arrow.style.borderTop = `5px solid ${color}`;
                arrow.style.left = `${left - 5}px`;
                arrow.style.top = `${top - 2}px`;
                arrow.style.transform = 'rotate(90deg)';
                break;
            case 'up':
                arrow.style.borderTop = `5px solid ${color}`;
                arrow.style.left = `${left - 5}px`;
                arrow.style.top = `${top - 2}px`;
                arrow.style.transform = 'rotate(270deg)';
                break;
        }

        return arrow;
    };


    const clearDependencyMarkers = () => {
        const markers = document.querySelectorAll('.dependency-marker, .dependency-arrow');
        markers.forEach(marker => marker.remove());

        const svgPaths = document.querySelectorAll('.gantt-dependency-lines path');
        svgPaths.forEach(path => path.remove());
    };

    const calculateWidth = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffInDays = Math.max(1, differenceInDays(end, start) +1);

        // Günlük modda her gün için 80px genişlik, haftalık modda her gün için 20px genişlik
        const dayWidth = viewMode === 'daily' ? 80 : 20;
        return diffInDays * dayWidth + 'px'; // Günlük veya haftalık genişlik ayarlaması
    };



    const calculateLeftPosition = (startDate) => {
        const start = new Date(startDate);
        const minDate = new Date(Math.min(...tasks.map(task => new Date(task.start_date))));

        if (!isValidDate(start)) {
            
            return 0;
        }

        let leftPosition = 0;

        if (viewMode === 'daily') {
            // Günlük mod: Gün farkı kullan
            const diffInDays = Math.max(0, differenceInDays(start, minDate));
            leftPosition = diffInDays * 80; // Günlük modda her gün için 80px genişlik
        } else if (viewMode === 'weekly') {
            // Haftalık mod: Önce hafta farkını hesapla
            const diffInWeeks = Math.max(0, differenceInWeeks(startOfWeek(start, { weekStartsOn: 1 }), startOfWeek(minDate, { weekStartsOn: 1 })));
            leftPosition = diffInWeeks * 140; // Her hafta için 140px (7 gün x 20px)

            // Haftalık modda, gün farkını hesaba kat (örneğin haftanın başı Pazartesi ise, Çarşamba görevi biraz sağda başlasın)
            const startOfCurrentWeek = startOfWeek(start, { weekStartsOn: 1 });
            const daysIntoWeek = differenceInDays(start, startOfCurrentWeek); // Haftanın başından kaç gün geçtiğini hesapla
            leftPosition += daysIntoWeek * 20; // Gün başına 20px ekle
        }

        return leftPosition;
    };


    const isValidDate = (date) => {
        return date instanceof Date && !isNaN(date.getTime());
    };

    const toggleSubtasks = async (taskId) => {
        clearDependencyMarkers();  // Clear all previous dependency lines first.

        if (openTasks[taskId]) {
            // If the subtasks are already open, just toggle them off.
            setOpenTasks(prev => {
                const updatedOpenTasks = { ...prev, [taskId]: !prev[taskId] };
                return updatedOpenTasks;
            });
        } else {
            // Fetch the subtasks data when opening the subtasks.
            try {
                const token = localStorage.getItem('token');
                const response = await api.get(`/api/subtasks/gantt/${taskId}/subtasks`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const subtasksData = response.data;


                // Add the subtasks to state.
                setSubtasks(prev => ({ ...prev, [taskId]: subtasksData }));
                setOpenTasks(prev => ({ ...prev, [taskId]: true }));

                // Fetch the relevant subtask dependencies.
                const relevantSubtaskDependencies = await fetchAllSubtaskDependencies(subtasksData);


                // Filter out existing dependencies for the task, then add the relevant ones.
                setSubtaskDependencies(prev => {
                    // Remove any existing dependencies related to this taskId
                    const filteredDependencies = prev.filter(dep =>
                        !subtasksData.some(subtask =>
                            dep.subtaskId === subtask.id || dep.dependentSubtaskId === subtask.id
                        )
                    );
                    // Append the relevant dependencies for the current task
                    return [...filteredDependencies, ...relevantSubtaskDependencies];
                });



            } catch (error) {
                
            }
        }
        // After state updates, `useLayoutEffect` will handle drawing the dependencies.
    };

    const sortTasksByDependencies = (tasks, dependencies = []) => {

        // İlk olarak başlangıç tarihine göre sıralıyoruz
        const reorderedTasks = [...tasks].sort((a, b) => {
            const startA = new Date(a.start_date);
            const startB = new Date(b.start_date);
            return startA - startB; // Tarihe göre sıralama (erken tarih önce)
        });

        // Task'ları ID ile kolay erişmek için bir map oluşturuyoruz
        const taskMap = new Map();
        reorderedTasks.forEach(task => taskMap.set(task.id, task));

        

        return reorderedTasks;
    };

    const handleMouseEnter = (taskId) => {
        // Hem bar hem de ilgili label'ı vurgula
        const barElement = document.querySelector(`[data-task-id="${taskId}"]`);
        const labelElement = document.querySelector(`[data-task-id="${taskId}"]`);

        if (barElement) {
            barElement.classList.add('highlight');
        }
        if (labelElement) {
            labelElement.classList.add('highlight');
        }
    };

    const handleMouseLeave = (taskId) => {
        // Hem bar hem de ilgili label'ın vurgusunu kaldır
        const barElement = document.querySelector(`[data-task-id="${taskId}"]`);
        const labelElement = document.querySelector(`[data-task-id="${taskId}"]`);

        if (barElement) {
            barElement.classList.remove('highlight');
        }
        if (labelElement) {
            labelElement.classList.remove('highlight');
        }
    };

    // Task için details modalini açan fonksiyon
    const handleOpenTaskDetailsModal = (task) => {
        setSelectedTask(task);  // Seçili task'ı ayarla
        setSelectedSubtask(null);  // Subtask'ı sıfırla
        setDetailsModalOpen(true);  // Modal'i aç
    };

    // Subtask için details modalini açan fonksiyon
    const handleOpenSubtaskDetailsModal = (subtask) => {
        setSelectedSubtask(subtask);  // Seçili subtask'ı ayarla
        setSelectedTask(null);  // Task'ı sıfırla
        setDetailsModalOpen(true);  // Modal'i aç
    };

    const fetchUserRole = async (projectId, token) => {
        try {
            // API isteğini yap
            const roleResponse = await api.get(`/api/projects/${projectId}/role`, {
                headers: { Authorization: `Bearer ${token}` }
            });


            setUserRoleId(roleResponse.data.roleId)
        } catch (error) {
            // Hata oluşursa yanıtı veya hata mesajını yazdır
            console.error('Error fetching role:', error.response?.data || error.message);
        }
    };



    const isWeekend = (day) => {
        // Eğer `day` bir tarih objesi ise
        if (day instanceof Date) {
            return day.getDay() === 0 || day.getDay() === 6; // Pazar (0) veya Cumartesi (6)
        }

        // `day` bir string ise (`Mon, Tue, Wed` veya `M, T, W` gibi)
        if (typeof day === 'string') {
            // Günlük mod için `Mon, Tue` gibi tam kısaltmalar
            if (['sat', 'sun'].includes(day.toLowerCase())) {
                return true;
            }

            // Haftalık modda `M, T, W` gibi tek harf kontrolü
            return day.toUpperCase() === 'S'; // `S` harfini (Cumartesi ve Pazar) kontrol et
        }

        // Eğer `day` beklenmeyen bir formatta geliyorsa
        return false;
    };




    const calculateWeekendColPosition = (index) => {
        if (viewMode === 'daily') {
            return index * 80 + 'px'; // Günlük modda her gün 80px
        }
        if (viewMode === 'weekly') {
            return index * 20 + 'px'; // Haftalık modda her sütun 140px
        }
        return '0px';
    };

    const flattenedBottomRow = Array.isArray(timeline?.bottomRow) ? timeline.bottomRow.flat() : [];



    return (
        <div className={`gantt-container ${viewMode}`}>
            <ToastContainer />

            {tasks.length === 0 ? (
                <Card
                style={{
                  backgroundColor: "#f9f9f9",
                  padding: "20px",
                  marginTop: "20px",
                  textAlign: "center",
                  border: "1px solid #4caf50",
                  borderRadius: "15px",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h4"
                    gutterBottom
                    style={{ color: "#4caf50", fontWeight: "bold" }}
                  >
                    🌟 {t("gantt.ready_to_start")}
                  </Typography>
                  <Typography
                    variant="body1"
                    style={{
                      marginBottom: "20px",
                      color: "#4caf50",
                      fontSize: "1.1rem",
                    }}
                  >
                    {t("gantt.no_tasks")} 🚀
                  </Typography>
                  <Button
                    variant="contained"
                    className="action-button"
                    color="primary"
                    onClick={() => navigate(`/projects/${projectId}`)}
                    style={{ padding: "10px 20px", fontSize: "1rem" }}
                  >
                    {t("gantt.go_dashboard")}
                  </Button>
                </CardContent>
              </Card>
              
            ) : (
                // Eğer görev varsa, normal Gantt Chart JSX'i burada gösterilmeye devam eder.
                <>
                    <div className="gantt-grid-container">
                        <div className="gantt-label-column">
                            <div className="view-switch">
                                <Typography variant="subtitle1" style={{ margin: '0 5px', fontWeight: 'bold' }}>{t("gantt.daily_view")}</Typography>
                                <Switch
                                    checked={viewMode === 'weekly'}
                                    onChange={handleViewModeChange}
                                    name="viewMode"
                                    color="primary"
                                />
                                <Typography variant="subtitle1" style={{ margin: '0 5px', fontWeight: 'bold' }}>{t("gantt.weekly_view")}</Typography>
                            </div>
                            {tasks.map((task, index) => (
                                <div key={index}>
                                    <div
                                        className={`gantt-task-label-row ${highlightedTask === task.id ? 'highlighted' : ''}`}

                                        onMouseEnter={() => setHighlightedTask(task.id)}
                                        onMouseLeave={() => setHighlightedTask(null)}
                                        ref={el => taskRefs.current[task.id] = el}
                                        data-task-id={task.id}
                                        data-start-date={task.start_date}
                                        data-due-date={task.due_date}
                                    >
                                        <IconButton className="task-buttons" style={{ float: 'right' }} onClick={() => handleOpenTaskDetailsModal(task)}>
                                        <MenuIcon />
                                        </IconButton>
                                        <Tooltip onClick={() => toggleSubtasks(task.id)} title={task.title.length > 30 ? task.title : ''}>
                                            <Typography className="gantt-task-label">
                                                <span className='ganttArrow'>{(openTasks[task.id] ? "▲" : "▼")}</span> {truncateText(task.title, 30)}
                                            </Typography>
                                        </Tooltip>

                                    </div>

                                    {openTasks[task.id] && subtasks[task.id] && subtasks[task.id].map((subtask, subIndex) => (
                                        <div
                                            key={subIndex}
                                            className={`gantt-subtask-label-row ${highlightedSubtask === subtask.id ? 'highlighted' : ''}`}
                                            onMouseEnter={() => setHighlightedSubtask(subtask.id)}
                                            onMouseLeave={() => setHighlightedSubtask(null)}
                                            ref={el => subtaskRefs.current[subtask.id] = el}
                                            data-task-id={subtask.id}
                                            data-start-date={subtask.start_date}
                                            data-due-date={subtask.due_date}
                                        >
                                            <Tooltip title={subtask.title.length > 30 ? subtask.title : ''}>
                                                <Typography className="gantt-subtask-label">
                                                    {truncateText(subtask.title, 30)}
                                                </Typography>
                                            </Tooltip>
                                            <IconButton className="task-buttons" onClick={() => handleOpenSubtaskDetailsModal(subtask)}>
                                            <MenuIcon />
                                            </IconButton>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        <div className="gantt-timeline-column">

                            <div className="timeline-header">

                                {/* Üst satır: Tarihler veya haftalık başlıklar */}
                                <div className="timeline-row top-row">
                                    {timeline.topRow && timeline.topRow.map((date, index) => (
                                        <div
                                            key={index}
                                            className={`timeline-cell ${isWeekend(date) ? 'weekend' : ''}`} // Cumartesi veya Pazar ise weekend class'ı ekle
                                            style={{ width: viewMode === 'weekly' ? '140px' : '80px' }}
                                        >
                                            {date}
                                        </div>
                                    ))}
                                </div>

                                <div className="timeline-row bottom-row">
                                    {timeline.bottomRow && timeline.bottomRow.map((day, index) => (
                                        <div
                                            key={index}
                                            className={`timeline-cell ${isWeekend(day) ? 'weekend' : ''}`} // Cumartesi veya Pazar günlerini doğru şekilde işaretle
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: viewMode === 'weekly' ? '140px' : '80px',
                                                padding: '0'
                                            }}
                                        >
                                            {Array.isArray(day) ? day.map((d, i) => (
                                                <div
                                                    key={i}
                                                    style={{
                                                        width: '20px',
                                                        display: 'inline-block',
                                                        borderLeft: i === 0 ? 'none' : '1px solid #ddd',
                                                        backgroundColor: isWeekend(d) ? '#c4d8fa' : '' // Cumartesi ve Pazar günlerine hafif arka plan
                                                    }}
                                                >
                                                    {d}
                                                </div>
                                            )) : day}
                                        </div>
                                    ))}
                                </div>

                            </div>

                            <div className="gantt-tasks">
                                {flattenedBottomRow && flattenedBottomRow.map((day, index) => {
                                    //console.log(`Flattened Day: ${day}, Index: ${index}, ViewMode: ${viewMode}`);
                                    const isWeekendDay = isWeekend(day); // Her bir günü ayrı olarak kontrol et
                                    //console.log(`Day: ${day}, Index: ${index}, isWeekend: ${isWeekendDay}, ViewMode: ${viewMode}`);

                                    return isWeekendDay && (
                                        <div
                                            key={`weekend-overlay-${index}`}
                                            className={`weekend-column-overlay ${viewMode}`}
                                            style={{
                                                left: calculateWeekendColPosition(index),
                                                width: viewMode === 'daily' ? '80px' : '20px',
                                                height: '100%',
                                                backgroundColor: 'rgb(215, 225, 248)',
                                                position: 'absolute',
                                                top: 0,
                                                zIndex: 0, // Diğer div'lerin altına koymak için düşük z-index kullanıyoruz
                                            }}
                                        />
                                    );
                                })}




                                <svg ref={linesContainerRef} className="gantt-dependency-lines"></svg>
                                {tasks.map((task, index) => (
                                    <div key={index}>
                                        <div
                                            className={isWeekend(task.start_date) ? 'gantt-task-dailyRow' : 'gantt-task-row'}

                                            data-task-id={task.id}
                                            data-start-date={task.start_date}
                                            data-due-date={task.due_date}
                                        >
                                            <div
                                                className="gantt-task-bar"
                                                style={getBarStyle(task.start_date, task.due_date, task.status)}
                                                data-task-id={task.id}
                                                data-start-date={task.start_date}
                                                data-due-date={task.due_date}
                                                ref={el => taskRefs.current[task.id] = el}
                                                onMouseEnter={() => handleMouseEnter(task.id)} // Bar üzerine gelindiğinde çalışacak
                                                onMouseLeave={() => handleMouseLeave(task.id)} // Bar üzerinden çıkıldığında çalışacak
                                            />
                                        </div>

                                        {openTasks[task.id] && subtasks[task.id] && subtasks[task.id].map((subtask, subIndex) => (
                                            <div
                                                key={subIndex}
                                                className="gantt-subtask-row"
                                                data-task-id={subtask.id}
                                                data-start-date={subtask.start_date}
                                                data-due-date={subtask.due_date}
                                            >
                                                <div
                                                    className="gantt-subtask-bar"
                                                    style={getBarStyle(subtask.start_date, subtask.due_date, subtask.status)}
                                                    data-task-id={subtask.id}
                                                    data-start-date={subtask.start_date}
                                                    data-due-date={subtask.due_date}
                                                    ref={el => subtaskRefs.current[subtask.id] = el}
                                                    onMouseEnter={() => handleMouseEnter(subtask.id)} // Subtask barına gelindiğinde çalışacak
                                                    onMouseLeave={() => handleMouseLeave(subtask.id)} // Subtask barından çıkıldığında çalışacak
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                </>
            )}

            {/* Görünüm değiştirme butonu */}


            <DetailsForm
                task={selectedTask}
                subtask={selectedSubtask}
                teams={teams}
                open={detailsModalOpen}
                onClose={() => setDetailsModalOpen(false)}
                projectId={projectId}
                currentUserRoleId={userRoleId}
                detailsTab={detailsTab}
            />
        </div>
    );
};

export default Gantt;
